<template>
  <div v-if="!initialized">
    <b-loading :model-value="true" />
  </div>
  <Modal
    v-else
    :has_parent="false"
    :is_show_footer="false"
    :is_show_header="false"
    :is_small="false"
    @actionCancel="$parent.$parent.close"
    @actionPrimary="$parent.$parent.close"
  >
    <template #content>
      <div class="bg-white">
        <header class="media">
          <div class="media-left grow">
            <div class="content">
              <div class="title is-5">
                {{ $t('navbar.topnavbar.selectTeam') }}
              </div>
              <div class="subtitle is-6 text-gray-500">
                {{ email_address }}
              </div>
            </div>
          </div>
          <div class="media-right">
            <img
              alt="logo-ziggu"
              src="../../../assets/images/Ziggu_Logo_Green.png"
              style="width: 100px"
            />
          </div>
        </header>

        <SelectTenant />

        <footer
          v-if="!$_.isEmpty(current_user)"
          class="mt-4 flex"
          style="justify-content: flex-end"
        >
          <ButtonTertiary
            action="sign_out"
            @onClick="emitter.$emit('logout')"
          >
            {{ $t('navbar.topnavbar.logout') }}
          </ButtonTertiary>
        </footer>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import Modal from '@/app/shared_components/modals/TheModal.vue';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import { RECORD_TYPE } from '@/app/data/model_constants';
import { LOGIN_PAGE_NOTIFICATIONS } from '@/app/data/general_constants';
import { ZIGGU_PARENT_URL, zigguProfileTenantURL } from '@/app/util/urls';
import SelectTenant from '@/app/shared_components/SelectTenant.vue';

const {
  current_is_impersonation,
  current_profile,
  current_user,
  current_user_customers,
  current_user_employees,
  current_user_partners,
  current_user_supervisors,
  current_user_projects,
  current_user_tenants,
  current_user_profiles,
} = mapGetters([
  'current_is_impersonation',
  'current_profile',
  'current_user',
  'current_user_customers',
  'current_user_employees',
  'current_user_partners',
  'current_user_supervisors',
  'current_user_projects',
  'current_user_tenants',
  'current_user_profiles',
]);

const { FETCH_USER_PROFILES, LOGOUT_USER } = mapActions(['FETCH_USER_PROFILES', 'LOGOUT_USER']);

const initialized = ref(false);

const is_parent_domain = computed(() => ZIGGU_PARENT_URL === window.location.origin);

const email_address = computed(() =>
  is_parent_domain.value ? current_user.value.attributes.email_address : current_profile.value.attributes.email_address,
);

const profiles = computed(() => {
  const profiles_by_type = [
    Object.values(current_user_supervisors.value),
    Object.values(current_user_employees.value),
    Object.values(current_user_customers.value),
    Object.values(current_user_partners.value),
  ];
  return [].concat(...profiles_by_type);
});

function navigateToLogin({ email = null, notification = null, redirect = null } = {}) {
  const params = [];
  if (email) params.push(`email=${encodeURIComponent(email)}`);
  if (notification) params.push(`notification=${encodeURIComponent(notification)}`);
  if (redirect) params.push(`redirect=${encodeURIComponent(redirect)}`);

  let url = `${ZIGGU_PARENT_URL}/login`;
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }

  window.location.href = url;
}

function getProfile(tenant) {
  if (tenant.attributes.name === 'supervisor') return _.values(current_user_supervisors.value)[0];

  const client_id =
    tenant.attributes.tenantable_type === RECORD_TYPE.PROJECT
      ? current_user_projects.value[tenant.attributes.tenantable_id].attributes.client_id
      : tenant.attributes.tenantable_id;
  return _.find(profiles.value, ['attributes.client_id', client_id]);
}

async function fetchProfiles() {
  await FETCH_USER_PROFILES({});
  const profiles_count = _.size(current_user_profiles.value);

  if (profiles_count === 1 && is_parent_domain.value) {
    // skip profile switcher on TLD for single-profile users, redirect straight to appropriate tenant
    const tenant = _.values(current_user_tenants.value)[0];
    const profile = getProfile(tenant);
    let url = zigguProfileTenantURL(profile, tenant);

    // append meta query params on impersonation for non-supervisor profiles
    if (current_is_impersonation.value && tenant.attributes.subdomain !== 'supervisor') {
      url += `?_profile_type=${profile.type}&_profile_id=${profile.id}`;
    }

    window.location.href = url;
    return;
  } else if (profiles_count === 0) {
    // retrieve and append any (present) ziggu parameters to pass onto login page
    const email = _.get(current_user.value, 'attributes.email_address', '');
    await LOGOUT_USER({});
    navigateToLogin({
      email,
      notification: LOGIN_PAGE_NOTIFICATIONS.ERROR_NO_ACCESS,
    });
    return;
  }

  initialized.value = true;
}

fetchProfiles();
</script>
